import { createContext, type ReactNode, useContext, useState } from 'react';
import { useBangCanDoiPhatSinhCongNoExport } from './hooks/useBangCanDoiPhatSinhCongNoExport';
import { useDynamicSearch } from '@/hooks/use-dynamic-search';
import { getTableColumns } from './cols-definition';
import { useSearchFieldStates } from './hooks';
import { useReport, useRows } from '@/hooks';
import { QUERY_KEYS } from '@/constants';
import { initialValues } from './schema';

interface BangCanDoiPhatSinhCongNoContextValue {
  exportToExcel: () => Promise<void>;
  canExport: boolean;
  isExporting: boolean;
  queryParams: MuaHangRecord | null;
  setQueryParams: (params: MuaHangRecord | null) => void;

  getReport: (params: MuaHangRecord) => Promise<void>;
  isLoading: boolean;
  data: MuaHangRecord[];
  currentPage: number;
  handlePageChange: (page: number) => Promise<void>;
  totalItems: number;

  searchFieldStates: ReturnType<typeof useSearchFieldStates>;

  initialSearchDialogOpen: boolean;
  editPrintTemplateDialogOpen: boolean;
  showTable: boolean;
  searchParams: MuaHangRecord;
  handleInitialSearch: (formData: MuaHangRecord, getReport: (params: MuaHangRecord) => Promise<void>) => Promise<void>;
  handleInitialSearchClose: () => void;
  handleSearchClick: () => void;
  handleEditPrintTemplateClick: () => void;
  handleClosePrintTemplateDialog: () => void;
  handleSavePrintTemplate: (template: MuaHangRecord) => void;
  handleFixedColumnsClick: () => void;
  handleExportDataClick: () => void;

  selectedRowIndex: string | null;
  handleRowClick: (row: MuaHangRecord) => void;
  clearSelection: () => void;

  searchState: {
    filterParams: MuaHangRecord;
    searchResults: MuaHangRecord[];
    totalCount: number;
    currentPage: number;
    isSearching: boolean;
  };
  handleSearchChange: (values: MuaHangRecord) => void;
  handleSearchPageChange: (page: number) => void;
  hasFilterParams: boolean;
  handleInitialSearchLogic: (formData: MuaHangRecord) => Promise<void>;
  handleRefreshClickLogic: () => Promise<void>;
  isSearching: boolean;
}

const BangCanDoiPhatSinhCongNoContext = createContext<BangCanDoiPhatSinhCongNoContextValue | null>(null);

export const useBangCanDoiPhatSinhCongNoContext = () => {
  const context = useContext(BangCanDoiPhatSinhCongNoContext);
  if (!context) {
    throw new Error('useBangCanDoiPhatSinhCongNoContext must be used within BangCanDoiPhatSinhCongNoProvider');
  }
  return context;
};

export const BangCanDoiPhatSinhCongNoProvider = ({ children }: { children: ReactNode }) => {
  const [queryParams, setQueryParams] = useState<MuaHangRecord | null>(null);
  const { exportToExcel, canExport, isExporting } = useBangCanDoiPhatSinhCongNoExport(queryParams);
  const { getReport, isLoading, data, currentPage, handlePageChange, totalItems } = useReport<
    MuaHangRecord,
    MuaHangRecord
  >({
    endpoint: QUERY_KEYS.BANG_CAN_DOI_PHAT_SINH_CONG_NO,
    pageSize: 50
  });

  const searchFieldStates = useSearchFieldStates();
  const dialogState = useDialogState({ setQueryParams, exportToExcel });

  const { selectedRowIndex, handleRowClick, clearSelection } = useRows();
  const {
    searchState,
    handleSearchChange,
    handleSearchPageChange,
    hasFilterParams,
    handleInitialSearch: handleInitialSearchLogic,
    handleRefreshClick: handleRefreshClickLogic,
    isSearching
  } = useDynamicSearch({
    onSearch: getReport,
    params: dialogState.searchParams,
    onInitialSearch: dialogState.handleInitialSearch,
    getColumns: () => getTableColumns(() => {}),
    endpoint: QUERY_KEYS.BANG_CAN_DOI_PHAT_SINH_CONG_NO,
    pageSize: 50
  });

  return (
    <BangCanDoiPhatSinhCongNoContext.Provider
      value={{
        exportToExcel,
        canExport,
        isExporting,
        queryParams,
        setQueryParams,

        getReport,
        isLoading,
        data,
        currentPage,
        handlePageChange,
        totalItems,

        searchFieldStates,

        ...dialogState,

        selectedRowIndex,
        handleRowClick,
        clearSelection,

        searchState,
        handleSearchChange,
        handleSearchPageChange,
        hasFilterParams,
        handleInitialSearchLogic,
        handleRefreshClickLogic,
        isSearching
      }}
    >
      {children}
    </BangCanDoiPhatSinhCongNoContext.Provider>
  );
};

function useDialogState({
  setQueryParams,
  exportToExcel
}: {
  setQueryParams: (params: MuaHangRecord | null) => void;
  exportToExcel: () => Promise<void>;
}) {
  const [initialSearchDialogOpen, setInitialSearchDialogOpen] = useState(true);
  const [editPrintTemplateDialogOpen, setEditPrintTemplateDialogOpen] = useState(false);
  const [showTable, setShowTable] = useState(false);
  const [searchParams, setSearchParams] = useState<MuaHangRecord>(initialValues);

  const handleInitialSearch = async (formData: MuaHangRecord, getReport: (params: MuaHangRecord) => Promise<void>) => {
    try {
      const searchState = {
        ...formData
      };

      const apiParams = {
        ...formData
      };

      setSearchParams(searchState);
      setShowTable(true);
      setQueryParams(apiParams);
      await getReport(apiParams);
      handleInitialSearchClose();
    } catch (error) {
      return;
    }
  };

  const handleInitialSearchClose = () => {
    setInitialSearchDialogOpen(false);
  };

  const handleSearchClick = () => {
    setInitialSearchDialogOpen(true);
  };

  const handleEditPrintTemplateClick = () => {
    setEditPrintTemplateDialogOpen(true);
  };

  const handleClosePrintTemplateDialog = () => {
    setEditPrintTemplateDialogOpen(false);
  };

  const handleSavePrintTemplate = (_template: MuaHangRecord) => {
    setEditPrintTemplateDialogOpen(false);
  };

  const handleFixedColumnsClick = () => {};

  const handleExportDataClick = () => exportToExcel();

  return {
    initialSearchDialogOpen,
    editPrintTemplateDialogOpen,
    showTable,
    searchParams,
    handleInitialSearch,
    handleInitialSearchClose,
    handleSearchClick,
    handleEditPrintTemplateClick,
    handleClosePrintTemplateDialog,
    handleSavePrintTemplate,
    handleFixedColumnsClick,
    handleExportDataClick
  };
}
